import type { ScanRequest } from '@sdd-maturity/shared-types';
import { ScanFooterLine } from './ScanFooterLine';

export interface ScanFormProps {
  onSubmit: (request: ScanRequest) => void;
  disabled?: boolean;
  /** Owning team, passed down by the Delivery phase page's inline scan panel. */
  teamNumber?: string;
  /** Most recent repo scan timestamp for the team; shows "last scanned on …". */
  lastScannedAt?: string | null;
  /** Alignment of the scan line; 'right' tucks it under the table's bottom-right. */
  align?: 'left' | 'right';
}

/**
 * Team-scan trigger. Given a selected team it renders the scan/re-scan line;
 * without one it prompts the user to pick a team in the header.
 *
 * The legacy URL/Org ad-hoc scan modes were removed (FR-4/S18): org/repo scans
 * persist scores correctly, but the dashboard only surfaces team-resolved data,
 * so their results were orphaned. Recover the deleted paths from git history when
 * a "recent ad-hoc scans" overview surface exists to display them.
 */
export function ScanForm({ onSubmit, disabled = false, teamNumber, lastScannedAt, align }: ScanFormProps) {
  const selectedTeamNumber = teamNumber ?? null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedTeamNumber) return;
    onSubmit({ type: 'team', teamNumber: selectedTeamNumber });
  }

  return (
    <form onSubmit={handleSubmit} data-testid="scan-form">
      <div className="flex flex-col gap-md">
        {selectedTeamNumber ? (
          <ScanFooterLine
            lastScannedAt={lastScannedAt}
            busy={disabled}
            onScan={() => onSubmit({ type: 'team', teamNumber: selectedTeamNumber })}
            testId="start-delivery-scan"
            ariaLabel="Scan team repositories"
            align={align}
          />
        ) : (
          <div
            className="text-base"
            style={{ color: 'var(--genai-sem-text-muted)', lineHeight: 1.5 }}
            data-testid="no-team-selected-hint"
          >
            <strong>Select a team in the header above to scan.</strong>
            <br />
            Org and repo-level scans are temporarily hidden — the dashboard
            currently only surfaces team-resolved data. Use the team selector
            in the page header to choose a team, then click Scan Now.
          </div>
        )}
      </div>
    </form>
  );
}
